import { useTranslation } from "react-i18next";

/**
 * Resolved labels for the V2 DIP screen, keyed by their dotted suffix under
 * `feature.dataImportProjects.v2`.
 *
 * i18next-cli extracts keys by static analysis and prunes anything it cannot
 * see (`removeUnusedKeys: true`). Building keys dynamically through variables
 * therefore drops them on the next extract. Listing every key here as a
 * literal `t()` call keeps them, while fixture-driven components look the text
 * up by suffix (`labels["status.clean"]`) without any dynamic `t()` of their own.
 *
 * `pipeline.step` is intentionally excluded: it needs `{{n}}` interpolation and
 * is called statically at its single use site.
 */
export function useDipLabels(): Record<string, string> {
  const { t } = useTranslation();
  return {
    pageSubtitle: t("feature.dataImportProjects.v2.pageSubtitle"),
    projectInfo: t("feature.dataImportProjects.v2.projectInfo"),

    "info.status": t("feature.dataImportProjects.v2.info.status"),
    "info.updated": t("feature.dataImportProjects.v2.info.updated"),
    "info.startDate": t("feature.dataImportProjects.v2.info.startDate"),
    "info.endDate": t("feature.dataImportProjects.v2.info.endDate"),
    "info.users": t("feature.dataImportProjects.v2.info.users"),
    "info.description": t("feature.dataImportProjects.v2.info.description"),

    "summary.title": t("feature.dataImportProjects.v2.summary.title"),
    "summary.dataSources": t("feature.dataImportProjects.v2.summary.dataSources"),
    "summary.data": t("feature.dataImportProjects.v2.summary.data"),
    "summary.ready": t("feature.dataImportProjects.v2.summary.ready"),
    "summary.evidences": t("feature.dataImportProjects.v2.summary.evidences"),
    "summary.drafts": t("feature.dataImportProjects.v2.summary.drafts"),
    "summary.latestEvidences": t("feature.dataImportProjects.v2.summary.latestEvidences"),
    "summary.changeRequests": t("feature.dataImportProjects.v2.summary.changeRequests"),
    "summary.changeRequestsOpen": t("feature.dataImportProjects.v2.summary.changeRequestsOpen"),
    "summary.changeRequestsReviewed": t("feature.dataImportProjects.v2.summary.changeRequestsReviewed"),
    "summary.changeRequestsApproved": t("feature.dataImportProjects.v2.summary.changeRequestsApproved"),
    "summary.changeRequestsRejected": t("feature.dataImportProjects.v2.summary.changeRequestsRejected"),
    "summary.records": t("feature.dataImportProjects.v2.summary.records"),
    "summary.entities": t("feature.dataImportProjects.v2.summary.entities"),
    "summary.relations": t("feature.dataImportProjects.v2.summary.relations"),

    "schema.title": t("feature.dataImportProjects.v2.schema.title"),
    "schema.configured": t("feature.dataImportProjects.v2.schema.configured"),

    "pipeline.title": t("feature.dataImportProjects.v2.pipeline.title"),
    excludeDeactivated: t("feature.dataImportProjects.v2.excludeDeactivated"),

    "section.import": t("feature.dataImportProjects.v2.section.import"),
    "section.compose": t("feature.dataImportProjects.v2.section.compose"),

    "steps.import": t("feature.dataImportProjects.v2.steps.import"),
    "steps.compose": t("feature.dataImportProjects.v2.steps.compose"),

    "stage.dataSources": t("feature.dataImportProjects.v2.stage.dataSources"),
    "stage.bejob": t("feature.dataImportProjects.v2.stage.bejob"),
    "stage.drafts": t("feature.dataImportProjects.v2.stage.drafts"),
    "stage.evidenceSets": t("feature.dataImportProjects.v2.stage.evidenceSets"),
    "stage.bcrjob": t("feature.dataImportProjects.v2.stage.bcrjob"),
    "stage.changeRequests": t("feature.dataImportProjects.v2.stage.changeRequests"),
    "stage.records": t("feature.dataImportProjects.v2.stage.records"),

    "peek.dataSource": t("feature.dataImportProjects.v2.peek.dataSource"),
    "peek.evidenceSet": t("feature.dataImportProjects.v2.peek.evidenceSet"),
    "peek.batchEvidencesJob": t("feature.dataImportProjects.v2.peek.batchEvidencesJob"),
    "peek.batchChangeRequestsJob": t("feature.dataImportProjects.v2.peek.batchChangeRequestsJob"),
    "peek.entityComposer": t("feature.dataImportProjects.v2.peek.entityComposer"),
    "peek.relationComposer": t("feature.dataImportProjects.v2.peek.relationComposer"),
    "peek.recordUpdateComposer": t("feature.dataImportProjects.v2.peek.recordUpdateComposer"),
    "peek.evidenceImporter": t("feature.dataImportProjects.v2.peek.evidenceImporter"),

    "status.clean": t("feature.dataImportProjects.v2.status.clean"),
    "status.check": t("feature.dataImportProjects.v2.status.check"),
    "status.ready": t("feature.dataImportProjects.v2.status.ready"),
    "status.imported": t("feature.dataImportProjects.v2.status.imported"),
    "status.open": t("feature.dataImportProjects.v2.status.open"),
    "status.changed": t("feature.dataImportProjects.v2.status.changed"),
    "status.committed": t("feature.dataImportProjects.v2.status.committed"),
    "status.pendingCorrections": t("feature.dataImportProjects.v2.status.pendingCorrections"),
    "status.pendingCorrectionsTooltip": t("feature.dataImportProjects.v2.status.pendingCorrectionsTooltip"),

    "stats.all": t("feature.dataImportProjects.v2.stats.all"),
    "stats.committed": t("feature.dataImportProjects.v2.stats.committed"),
    "stats.open": t("feature.dataImportProjects.v2.stats.open"),
    "stats.approved": t("feature.dataImportProjects.v2.stats.approved"),
    "stats.rejected": t("feature.dataImportProjects.v2.stats.rejected"),
    "stats.uncommitted": t("feature.dataImportProjects.v2.stats.uncommitted"),
  };
}
